import Yggdrasil from 'services/yggdrasil'
import { getFeedBackTemplate, getRecommendedMessageLink, textFormatter } from 'store/shared/chatBotHelper'
import { localStorageKeys } from 'store/plugins'
import { responseRoles } from 'services/yggdrasil/chat/chatClient'
import { RefreshChatToken } from 'services/yggdrasil/setter'
import { fetchFromLocalStorage } from 'helpers/localStorageHelper'
import { AxiosError } from 'axios'

const state = {
    chatSessions: null,
    singleChatHistory: null,
    showChatBot: false,
    currentChat: null,
    showFeedBackPanel: false,
    introWatched: fetchFromLocalStorage('evaChatbotIntroWatched', false),
    chatTokenExpiration: fetchFromLocalStorage(localStorageKeys.chatTokenExpiration, 0),
    chatTokenRefreshTime: fetchFromLocalStorage(localStorageKeys.chatTokenRefreshTime, 0)
}

const mutations = {
    ASSIGN_CHAT_HISTORY (state, chatHistory) {
        if (chatHistory === null) {
            state.singleChatHistory = null
        } else {
            let modifiedChatHistory = chatHistory.messages.map(chatMessage => {
                if (chatMessage.role === responseRoles.ASSISTANT) {
                    chatMessage.role = 'ai'
                }
                if (chatMessage.role === 'ai') {
                    chatMessage.html = `
                    ${textFormatter(chatMessage.content)}
                    ${getRecommendedMessageLink(chatMessage)}
                    ${getFeedBackTemplate(chatMessage)}
                    `
                } else {
                    chatMessage.text = chatMessage.content
                }
                delete chatMessage.content
                return chatMessage
            })
            state.singleChatHistory = modifiedChatHistory
        }
    },
    ASSIGN_CHAT_SESSIONS (state, chatSessions) {
        state.chatSessions = chatSessions
    },
    ASSIGN_CURRENT_CHAT (state, chat) {
        state.currentChat = chat
    },
    SET_CHAT_TOKEN_EXPIRATION (state, expirationTimestamp) {
        state.chatTokenExpiration = expirationTimestamp
    },
    SET_CHAT_TOKEN_REFRESH_TIME (state, refreshTime) {
        state.chatTokenRefreshTime = refreshTime
    },
    SET_INTRO_WATCHED (state, watched) {
        state.introWatched = watched
        if (watched === false) {
            window.localStorage.removeItem('evaChatbotIntroWatched')
        } else {
            window.localStorage.setItem('evaChatbotIntroWatched', true)
        }
    },
    SET_SHOW_CHATBOT (state, showChatBot) {
        state.showChatBot = showChatBot
    },
    SET_SHOW_FEEDBACK_PANEL (state, showFeedBackPanel) {
        state.showFeedBackPanel = showFeedBackPanel
    }
}
const actions = {
    async openNewChatSession (store) {
        const chat = await Yggdrasil.openNewChatSession()
        store.commit('ASSIGN_CURRENT_CHAT', chat)
        const chatSessions = await Yggdrasil.loadChatSessions()
        store.commit('ASSIGN_CHAT_SESSIONS', chatSessions)
        await store.dispatch('loadSingleChatHistory', chat.id)
    },
    async sendMessage (store, message) {
        const chatId = store.state.currentChat.id
        const chat = await Yggdrasil.sendMessage(chatId, message)
        return chat
    },
    async loadChatSessions (store) {
        let chatSessions = []
        try {
            chatSessions = await Yggdrasil.loadChatSessions()
        } catch (e) {
            if (e instanceof AxiosError && e.response.status === 404) {
                chatSessions = []
            }
        }
        store.commit('ASSIGN_CHAT_SESSIONS', chatSessions)
        if (chatSessions.length > 0) {
            store.commit('ASSIGN_CURRENT_CHAT', chatSessions[0])
        } else {
            store.commit('ASSIGN_CURRENT_CHAT', null)
        }
    },
    async loadSingleChatHistory (store, chatId) {
        const chatHistory = await Yggdrasil.loadSingleChatHistory(chatId)
        store.commit('ASSIGN_CHAT_HISTORY', chatHistory)
    },
    async refreshChatToken (store) {
        const refreshResult = await RefreshChatToken()
        store.commit('SET_CHAT_TOKEN_EXPIRATION', refreshResult.tokenExpirationTime)
        store.commit('SET_CHAT_TOKEN_REFRESH_TIME', (new Date()).getTime())
    },
    setChatTokenExpiration (store, expirationTimestamp) {
        store.commit('SET_CHAT_TOKEN_EXPIRATION', expirationTimestamp)
    },
    async setShowChatBot (store, showChatBot) {
        if (showChatBot && store.state.currentChat.id) {
            await store.dispatch('loadSingleChatHistory', store.state.currentChat.id)
        }
        store.commit('SET_SHOW_CHATBOT', showChatBot)
    },
    sendFeedback (store, feedback) {
        const feedbackPayload = {
            type: feedback.type,
            content: feedback.feedbackText
        }
        const chatId = store.state.currentChat.id
        Yggdrasil.sendChatBotFeedBack(feedback.messageId, chatId, feedbackPayload)
    },
    showFeedBackPanel (store, showFeedBackPanel) {
        store.commit('SET_SHOW_FEEDBACK_PANEL', showFeedBackPanel)
    },
    setIntroWatched (store, watched) {
        store.commit('SET_INTRO_WATCHED', watched)
    }
}

const getters = {
    getCurrentChat: state => {
        return state.currentChat
    },
    getChatHistory: state => {
        return state.singleChatHistory
    },
    getChatSessions: state => {
        return state.chatSessions
    },
    getChatTokenExpiration: state => {
        return state.chatTokenExpiration
    },
    getChatTokenRefreshTime: state => {
        return state.chatTokenRefreshTime
    },
    getShowChatBot: state => {
        return state.showChatBot
    },
    getShowFeedBackPanel: state => {
        return state.showFeedBackPanel
    },
    getIntroWatched: state => {
        return state.introWatched
    }
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}
